import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navigation from '../home/components/Navigation';

const AboutPage = () => {
  const values = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation',
      description: 'We push the boundaries of creative technology to deliver cutting-edge solutions.'
    },
    {
      icon: 'ri-star-line',
      title: 'Excellence',
      description: 'Every project receives our meticulous attention to detail and commitment to quality.'
    },
    {
      icon: 'ri-team-line',
      title: 'Collaboration',
      description: 'We work closely with clients to understand and exceed their creative vision.'
    },
    {
      icon: 'ri-rocket-line',
      title: 'Results',
      description: 'Our focus is on creating content that drives engagement and delivers measurable impact.'
    }
  ];

  const stats = [
    { number: '500+', label: 'Projects Completed' },
    { number: '98%', label: 'Client Satisfaction' },
    { number: '50M+', label: 'Views Generated' },
    { number: '24/7', label: 'Creative Support' }
  ];

  const services = [
    {
      title: 'AI Image Generation',
      description: 'Transform concepts into stunning visuals with advanced AI technology.',
      icon: 'ri-image-ai-line',
      link: '/services/ai-image-generation'
    },
    {
      title: 'AI Video Generation',
      description: 'Create breathtaking video content with AI-powered cinematography.',
      icon: 'ri-film-line',
      link: '/services/cinematic-videos'
    },
    {
      title: 'Hybrid AI Content',
      description: 'Enhance your content with AI-powered improvements and augmentation.',
      icon: 'ri-magic-line',
      link: '/services/hybrid-content'
    },
    {
      title: 'UGC Content',
      description: 'Authentic user-generated content that builds trust and engagement.',
      icon: 'ri-user-heart-line',
      link: '/services/ugc-content'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-40 pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/248cc360b02a0f902b1418584264bd9b.jpg"
            alt="Black Velvet Studio"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black"></div>
        </div>
        
        <div className="relative max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-brand-pink/20 to-brand-aqua/20 backdrop-blur-xl border border-brand-pink/30 mb-8">
              <i className="ri-building-line text-brand-pink text-xl"></i>
              <span className="text-sm font-semibold text-white">About Our Studio</span>
            </div>
            
            <h1 className="text-7xl font-light mb-8">
              <span className="text-white">Creative </span>
              <span className="bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent">Excellence</span>
              <br />
              <span className="text-white">Meets AI Innovation</span>
            </h1>
            
            <p className="text-2xl text-white/80 leading-relaxed mb-12">
              At Black Velvet Studio, we're pioneering the future of creative content through 
              AI-powered solutions that transform ideas into extraordinary visual experiences.
            </p>
            
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-5 bg-gradient-to-r from-brand-pink to-brand-aqua rounded-full font-bold text-lg whitespace-nowrap cursor-pointer"
              >
                Work With Us
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-12">
          <div className="grid grid-cols-4 gap-12">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-5xl font-light bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent mb-4">
                  {stat.number}
                </div>
                <p className="text-white/60 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-32">
        <div className="max-w-[1400px] mx-auto px-12">
          <div className="grid grid-cols-[1.2fr,1fr] gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-5xl font-light mb-8">
                <span className="text-white">Our </span>
                <span className="bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent">Story</span>
              </h2>
              
              <div className="space-y-6 text-xl text-white/70 leading-relaxed">
                <p>
                  Founded by creative visionaries who saw the potential of AI to revolutionize 
                  content creation, Black Velvet Studio emerged from a simple belief: technology 
                  should amplify human creativity, not replace it.
                </p>
                
                <p>
                  We've built a studio where cutting-edge artificial intelligence meets artistic 
                  excellence, creating a new paradigm for visual storytelling that's both 
                  innovative and deeply human.
                </p>
                
                <p>
                  Today, we partner with brands, creators, and visionaries to produce content 
                  that doesn't just capture attention—it creates lasting emotional connections 
                  and drives real business results.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Creative%20professionals%20working%20with%20AI%20technology%20in%20modern%20studio%2C%20hot%20pink%20and%20aqua%20accent%20lighting%2C%20collaborative%20workspace%2C%20innovative%20creative%20process%2C%20futuristic%20design%20tools%2C%20professional%20creative%20team%20environment&width=800&height=1000&seq=about-story-1&orientation=portrait"
                  alt="Our Creative Process"
                  className="w-full h-[600px] object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-32">
        <div className="max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl font-light mb-6">
              <span className="text-white">Our </span>
              <span className="bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent">Values</span>
            </h2>
            <p className="text-xl text-white/60 max-w-3xl mx-auto">
              The principles that guide everything we do and every project we create.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 gap-12">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ y: -10 }}
                className="p-12 rounded-3xl bg-gradient-to-b from-white/5 to-white/2 border border-white/10 backdrop-blur-xl"
              >
                <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-gradient-to-r from-brand-pink/20 to-brand-aqua/20 mb-8">
                  <i className={`${value.icon} text-4xl bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent`}></i>
                </div>
                <h3 className="text-2xl font-semibold text-white mb-6">{value.title}</h3>
                <p className="text-white/70 text-lg leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-32">
        <div className="max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl font-light mb-6">
              <span className="text-white">What We </span>
              <span className="bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent">Create</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link to={service.link}>
                  <motion.div
                    whileHover={{ y: -10, scale: 1.02 }}
                    className="p-10 rounded-2xl bg-gradient-to-b from-white/5 to-white/2 border border-white/10 backdrop-blur-xl group cursor-pointer h-full"
                  >
                    <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-r from-brand-pink/20 to-brand-aqua/20 mb-6 group-hover:scale-110 transition-transform">
                      <i className={`${service.icon} text-3xl bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent`}></i>
                    </div>
                    <h3 className="text-2xl font-semibold text-white mb-4">{service.title}</h3>
                    <p className="text-white/70 leading-relaxed mb-6">{service.description}</p>
                    <div className="inline-flex items-center gap-2 text-brand-pink group-hover:gap-4 transition-all">
                      <span className="font-semibold">Learn More</span>
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </motion.div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32">
        <div className="max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-brand-pink/10 to-brand-aqua/10 border border-brand-pink/20 p-20 text-center"
          >
            <h2 className="text-5xl font-light text-white mb-6">
              Ready to Create Something Extraordinary?
            </h2>
            <p className="text-xl text-white/70 mb-10 max-w-2xl mx-auto">
              Let's collaborate to bring your vision to life with the power of AI-enhanced creativity.
            </p>
            <div className="flex items-center justify-center gap-6">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-12 py-5 bg-gradient-to-r from-brand-pink to-brand-aqua rounded-full text-white font-bold text-lg whitespace-nowrap cursor-pointer"
                >
                  Start a Project
                </motion.button>
              </Link>
              <Link to="/services">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-12 py-5 border-2 border-white/20 rounded-full text-white font-semibold text-lg whitespace-nowrap cursor-pointer hover:border-brand-pink transition-colors"
                >
                  View Services
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;